/**
 * Landing Page — Signup / Waitlist Form
 *
 * Registration-mode-aware form that replaces the old OAuth-only login block:
 *   - Open registration: full signup form (first/last name, email, password, DOB, community, EULA/TOS)
 *   - Closed (invite-only): waitlist form (email + name → registration_waitlist table)
 *   - Authenticated: "Discover now" button
 *
 * Uses the public registration-mode endpoint and public community endpoint (no auth required).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import type { RegistrationProfile } from '../../auth/AuthContext';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { getRegistrationMode } from '../../api/systemSettings';
import { API_BASE_URL } from '../../api/client';
import CommunityDeclarationFields from '../../Components/CommunityDeclaration/CommunityDeclarationFields';
import {
  fetchDeclarationPickerData,
  type DeclarationPickerData,
} from '../../Components/CommunityDeclaration/declarationTree';
import EulaModal from '../../Components/EulaModal/EulaModal';
import styles from './LandingSignupForm.module.css';

// ── Component ────────────────────────────────────────────────

export default function LandingSignupForm() {
  const { t } = useAppTranslation('landing');
  const navigate = useNavigate();
  const { login, emailRegister } = useAuth();

  // Registration mode
  const [regMode, setRegMode] = useState<'open' | 'closed' | 'loading'>('loading');
  // DEV-1413 (slice 2) — full geography declaration tree (inactive geos
  // included; geo_* only, minimal pre-auth payload per §8.11 ruling 2).
  const [pickerData, setPickerData] = useState<DeclarationPickerData>({
    entities: [],
    relationships: [],
  });
  const [showEmailForm, setShowEmailForm] = useState(false);
  // Read-only User Agreement viewer. In-page modal, never a link into
  // the app: /eula and /terms are not routes, and navigating into the
  // SPA boots the shell on any stale session in the browser (session-
  // leak bug, 2026-07-08). ToS has no separate document yet — the
  // Terms link shows the same User Agreement pending a dedicated doc.
  const [showEula, setShowEula] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const googleBtnClosedRef = useRef<HTMLDivElement>(null);

  // Form state — signup
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  // DEV-1413 — primary (home) declaration is REQUIRED at signup; operating
  // is optional and defaults to primary (§8.11 ruling 1; server also
  // defaults when omitted).
  const [homeCommunity, setHomeCommunity] = useState('');
  const [operatingCommunity, setOperatingCommunity] = useState('');
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);

  // Form state — waitlist
  const [wlEmail, setWlEmail] = useState('');
  const [wlName, setWlName] = useState('');

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Load registration mode + communities ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [mode, declTree] = await Promise.all([
          getRegistrationMode(),
          fetchDeclarationPickerData(),
        ]);
        if (cancelled) return;
        setRegMode(mode.allowPublicRegistration ? 'open' : 'closed');
        setPickerData(declTree);
      } catch {
        if (!cancelled) setRegMode('closed');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Google Sign-In: render Google's official button ──
  // Uses google.accounts.id.renderButton() which opens a proper popup
  // and works reliably on localhost (unlike One Tap / prompt()).
  // Renders into BOTH the open-mode ref and the closed-mode ref so
  // existing users can always sign in regardless of registration mode.
  useEffect(() => {
    if (regMode === 'loading') return;

    const google = (window as any).google;
    const clientId = (window as any).__L4E_GOOGLE_CLIENT_ID || '';
    if (!google?.accounts?.id || !clientId) return;

    google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response: any) => {
        try {
          await login('google', response.credential);
          navigate('/', { replace: true });
        } catch (err: any) {
          setError(err.message || 'Google sign-in failed');
        }
      },
    });

    // Render into whichever container(s) are currently mounted
    const targets = [googleBtnRef.current, googleBtnClosedRef.current].filter(Boolean);
    for (const el of targets) {
      google.accounts.id.renderButton(el, {
        type: 'standard',
        theme: 'filled_blue',
        size: 'large',
        text: 'continue_with',
        width: el!.offsetWidth,
        shape: 'pill',
      });
    }
  }, [regMode, login, navigate]);

  // ── Signup handler ──
  const handleSignup = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!eulaAccepted || !tosAccepted) {
      setError('You must accept both the EULA and Terms of Service.');
      return;
    }
    if (!homeCommunity) {
      setError(t('signup.primaryCommunityRequiredError'));
      return;
    }

    setSubmitting(true);
    try {
      const profile: RegistrationProfile = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth,
        homeCommunityGuid: homeCommunity,
        // Optional — the register endpoint defaults operating to home
        // when omitted (§8.11 ruling 1).
        operatingCommunityGuid: operatingCommunity || undefined,
        eulaAccepted,
        tosAccepted,
      };
      await emailRegister(email, password, profile);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Registration failed.');
      setSubmitting(false);
    }
  }, [firstName, lastName, email, password, confirmPassword, dateOfBirth, homeCommunity, operatingCommunity, eulaAccepted, tosAccepted, emailRegister, navigate, t]);

  // ── Waitlist handler ──
  const handleWaitlist = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!wlEmail.trim()) {
      setError('Email is required.');
      return;
    }

    setSubmitting(true);
    try {
      // Use the register endpoint — it will capture in the waitlist table
      // and return 403 with waitlisted: true
      const res = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: wlEmail.trim().toLowerCase(),
          password: 'WaitlistPlaceholder1', // won't be stored — request fails at allow-list check
          firstName: wlName.trim().split(/\s+/)[0] || wlEmail.split('@')[0],
          lastName: wlName.trim().split(/\s+/).slice(1).join(' ') || '',
          dateOfBirth: '2000-01-01',
          eulaAccepted: true,
          tosAccepted: true,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (data.waitlisted || res.status === 403) {
        setSuccess("You're on the list! We'll let you know when your account is ready.");
        setWlEmail('');
        setWlName('');
      } else if (res.ok) {
        // Somehow registered (shouldn't happen in closed mode)
        setSuccess('Account created! Redirecting...');
        setTimeout(() => navigate('/', { replace: true }), 1000);
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [wlEmail, wlName, navigate]);

  // ── Loading ──
  if (regMode === 'loading') {
    return (
      <div className={styles.form}>
        <div className={styles.loadingDot} />
      </div>
    );
  }

  // ── Closed mode: Waitlist form + existing-user sign-in ──
  if (regMode === 'closed') {
    return (
      <div className={styles.form}>
        <h4 className={styles.title}>{t('signup.waitlistTitle')}</h4>
        <p className={styles.subtitle}>
          {t('signup.waitlistSubtitle')}
        </p>

        {success && <div className={styles.successMsg}>{success}</div>}
        {error && <div className={styles.errorMsg}>{error}</div>}

        {!success && (
          <form onSubmit={handleWaitlist} className={styles.fields}>
            <input
              type="text"
              placeholder={t('signup.waitlistNamePlaceholder')}
              value={wlName}
              onChange={e => setWlName(e.target.value)}
              className={styles.input}
              autoComplete="name"
            />
            <input
              type="email"
              placeholder={t('signup.waitlistEmailPlaceholder')}
              value={wlEmail}
              onChange={e => setWlEmail(e.target.value)}
              className={styles.input}
              autoComplete="email"
              required
            />
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={submitting || !wlEmail.trim()}
            >
              {submitting ? t('signup.waitlistSubmitting') : t('signup.waitlistSubmit')}
            </button>
          </form>
        )}

        {/* ── Existing user sign-in section ── */}
        <div className={styles.divider}>
          <span className={styles.dividerLine} />
          <span className={styles.dividerText}>{t('signup.alreadyMember')}</span>
          <span className={styles.dividerLine} />
        </div>

        <div ref={googleBtnClosedRef} className={styles.googleBtnContainer} />

        <p className={styles.loginLink}>
          {t('signup.signInPrompt')}{' '}
          <a href="/login" onClick={e => { e.preventDefault(); navigate('/login'); }}>
            {t('signup.signInLink')}
          </a>
        </p>
      </div>
    );
  }

  // ── Open mode: Full signup form ──
  // Calculate max DOB for 13+ age requirement
  const today = new Date();
  const maxDob = new Date(today.getFullYear() - 13, today.getMonth(), today.getDate())
    .toISOString().split('T')[0];

  return (
    <div className={styles.form}>
      <h4 className={styles.title}>Create Your Account</h4>
      <p className={styles.subtitle}>Join Life4Earth and start making an impact</p>

      {error && <div className={styles.errorMsg}>{error}</div>}

      {/* ── Google Sign-In (rendered by Google SDK) ── */}
      <div ref={googleBtnRef} className={styles.googleBtnContainer} />

      <div className={styles.divider}>
        <span className={styles.dividerLine} />
        <span className={styles.dividerText}>or sign up with email</span>
        <span className={styles.dividerLine} />
      </div>

      {/* ── Email signup toggle / form ── */}
      {!showEmailForm ? (
        <button
          type="button"
          className={styles.btnEmail}
          onClick={() => setShowEmailForm(true)}
        >
          Sign up with Email
        </button>
      ) : (
      <form onSubmit={handleSignup} className={styles.fields}>
        <div className={styles.row}>
          <input
            type="text"
            placeholder="First name *"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            className={styles.input}
            autoComplete="given-name"
            required
          />
          <input
            type="text"
            placeholder="Last name *"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            className={styles.input}
            autoComplete="family-name"
            required
          />
        </div>

        <input
          type="email"
          placeholder="Email address *"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className={styles.input}
          autoComplete="email"
          required
        />

        <div className={styles.row}>
          <input
            type="password"
            placeholder="Password *"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className={styles.input}
            autoComplete="new-password"
            required
            minLength={8}
          />
          <input
            type="password"
            placeholder="Confirm *"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            className={styles.input}
            autoComplete="new-password"
            required
          />
        </div>

        <div className={styles.row}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Date of birth *</label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={e => setDateOfBirth(e.target.value)}
              className={styles.input}
              max={maxDob}
              required
            />
          </div>
        </div>

        {/* DEV-1413 — primary + operating declaration over the full
            geography tree (inactive geos included; typeahead with
            breadcrumb paths). Primary required, operating optional. */}
        <CommunityDeclarationFields
          entities={pickerData.entities}
          relationships={pickerData.relationships}
          primaryValue={homeCommunity}
          onPrimaryChange={setHomeCommunity}
          operatingValue={operatingCommunity}
          onOperatingChange={setOperatingCommunity}
          primaryLabel={t('signup.primaryCommunityLabel')}
          operatingLabel={t('signup.operatingCommunityLabel')}
          operatingHint={t('signup.operatingCommunityHint')}
          placeholder={t('signup.communityPlaceholder')}
        />

        <div className={styles.checkboxGroup}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={eulaAccepted}
              onChange={e => setEulaAccepted(e.target.checked)}
              required
            />
            <span>I accept the <button type="button" className={styles.legalLink} onClick={(e) => { e.preventDefault(); setShowEula(true); }}>End User License Agreement</button> *</span>
          </label>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={tosAccepted}
              onChange={e => setTosAccepted(e.target.checked)}
              required
            />
            <span>I accept the <button type="button" className={styles.legalLink} onClick={(e) => { e.preventDefault(); setShowEula(true); }}>Terms of Service</button> *</span>
          </label>
        </div>

        <button
          type="submit"
          className={styles.btnPrimary}
          disabled={submitting || !firstName || !lastName || !email || !password || !confirmPassword || !dateOfBirth || !homeCommunity || !eulaAccepted || !tosAccepted}
        >
          {submitting ? 'Creating account...' : 'Create Account'}
        </button>

        <p className={styles.loginLink}>
          Already have an account?{' '}
          <a href="/login" onClick={e => { e.preventDefault(); navigate('/login'); }}>Sign in</a>
        </p>
      </form>
      )}

      {/* Read-only User Agreement viewer — acceptance happens via the
          checkboxes + register call, not here. */}
      <EulaModal
        open={showEula}
        viewOnly
        onAccepted={() => {}}
        onAbort={() => setShowEula(false)}
      />
    </div>
  );
}
